class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event, this.dataset.type == 'remove');
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getQuantityInputs(id) {
    return [
      this.querySelector(`#Quantity-${id}`),
      this.querySelector(`#Quantity-${id}-mobile`),
      this.querySelector(`#Drawer-quantity-${id}`),
    ].filter(Boolean);
  }

  resetQuantityInput(id) {
    const inputs = this.getQuantityInputs(id);
    const fallbackValue =
      inputs.find((input) => input.getAttribute('value') !== null)?.getAttribute('value') ??
      inputs[0]?.value;

    if (typeof fallbackValue === 'undefined') {
      this.isEnterPressed = false;
      return;
    }

    inputs.forEach((input) => {
      input.value = fallbackValue;
    });

    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    if (typeof event.target.select === 'function') {
      event.target.select();
    }
  }

  parseCartError(error) {
    if (typeof error !== 'string') {
      return {
        type: '',
        message: error ? String(error) : '',
      };
    }

    const structuredErrorMatch = error.match(/@type:(.*?)@message:([\s\S]*)/);

    if (!structuredErrorMatch) {
      return {
        type: '',
        message: error.trim(),
      };
    }

    return {
      type: structuredErrorMatch[1].trim(),
      message: structuredErrorMatch[2].trim(),
    };
  }

  hasPurchaseLimitConstraint(input) {
    const purchaseLimitMax = parseInt(input?.dataset?.purchaseLimitMax, 10);
    return !Number.isNaN(purchaseLimitMax);
  }

  hasSkuQuotaConstraint(input) {
    const skuQuotaMax = parseInt(input?.dataset?.skuQuotaMax, 10);
    const skuQuotaLimit = parseInt(input?.dataset?.skuQuotaLimit, 10);
    return !Number.isNaN(skuQuotaMax) || !Number.isNaN(skuQuotaLimit);
  }

  getNormalizedSkuQuotaQuantity(input, requestedQuantity) {
    const skuQuotaMax = parseInt(input?.dataset?.skuQuotaMax, 10);
    const normalizedRequestedQuantity = parseInt(requestedQuantity, 10);

    if (Number.isNaN(skuQuotaMax) || Number.isNaN(normalizedRequestedQuantity)) {
      return requestedQuantity;
    }

    return Math.max(0, Math.min(normalizedRequestedQuantity, skuQuotaMax));
  }

  getNormalizedSkuQuotaLimitQuantity(input, requestedQuantity) {
    const skuQuotaLimit = parseInt(input?.dataset?.skuQuotaLimit, 10);
    const normalizedRequestedQuantity = parseInt(requestedQuantity, 10);

    if (Number.isNaN(skuQuotaLimit) || Number.isNaN(normalizedRequestedQuantity)) {
      return requestedQuantity;
    }

    return Math.max(0, Math.min(normalizedRequestedQuantity, skuQuotaLimit));
  }

  getSuggestedQuantityFromErrorMessage(message) {
    if (typeof message !== 'string') return null;

    const matches = message.match(/\d+/g);
    if (!matches?.length) return null;

    const suggestedQuantity = parseInt(matches[0], 10);
    return Number.isNaN(suggestedQuantity) ? null : suggestedQuantity;
  }

  getNormalizedPurchaseLimitQuantity(input, requestedQuantity) {
    const purchaseLimitMax = parseInt(input?.dataset?.purchaseLimitMax, 10);
    const normalizedRequestedQuantity = parseInt(requestedQuantity, 10);

    if (Number.isNaN(purchaseLimitMax) || Number.isNaN(normalizedRequestedQuantity)) {
      return requestedQuantity;
    }

    return Math.max(0, Math.min(normalizedRequestedQuantity, purchaseLimitMax));
  }

  setQuantityInputValues(line, quantity) {
    this.getQuantityInputs(line).forEach((input) => {
      input.value = quantity;
      input.setAttribute('value', quantity);
    });
  }

  getQuantityInputMax(input) {
    const inputMax = parseInt(input?.dataset?.max ?? input?.getAttribute('max'), 10);
    return Number.isNaN(inputMax) ? null : inputMax;
  }

  validateQuantity(event) {    
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    const bypassLocalMaxValidation = this.hasPurchaseLimitConstraint(event.target) || this.hasSkuQuotaConstraint(event.target);
    const inputMax = this.getQuantityInputMax(event.target);
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (!bypassLocalMaxValidation && inputMax !== null && inputValue > inputMax) {
      message = window.quickOrderListStrings.max_error.replace('[max]', inputMax);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message && this.dataset.type !== 'remove') {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id || document.querySelector('.cart__footer'),
        selector: ['.js-contents', '.js-contents-checkout'],
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Quantity-${line}-mobile`) || document.getElementById(`Drawer-quantity-${line}`);
          const quantityElement_mobile = document.getElementById(`Quantity-${line}-mobile`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            const { type: errorType, message: errorMessage } = this.parseCartError(parsedState.errors);
            const normalizedSkuQuotaQuantityFromInput = quantityElement
              ? this.getNormalizedSkuQuotaQuantity(quantityElement, quantity)
              : quantity;
            const normalizedSkuQuotaQuantityFromLimit = quantityElement
              ? this.getNormalizedSkuQuotaLimitQuantity(quantityElement, quantity)
              : quantity;
            const normalizedSkuQuotaQuantityFromMessage = this.getSuggestedQuantityFromErrorMessage(errorMessage);
            const normalizedSkuQuotaQuantity =
              normalizedSkuQuotaQuantityFromInput !== quantity
                ? normalizedSkuQuotaQuantityFromInput
                : normalizedSkuQuotaQuantityFromLimit !== quantity
                  ? normalizedSkuQuotaQuantityFromLimit
                  : normalizedSkuQuotaQuantityFromMessage ?? normalizedSkuQuotaQuantityFromInput;
            const normalizedPurchaseLimitQuantity = quantityElement
              ? this.getNormalizedPurchaseLimitQuantity(quantityElement, quantity)
              : quantity;

            if (errorType === 'sku-quota' && normalizedSkuQuotaQuantity !== quantity) {
              this.setQuantityInputValues(line, normalizedSkuQuotaQuantity);
              this.disableLoading(line);
              this.updateQuantity(line, normalizedSkuQuotaQuantity, event, name, variantId);
              return;
            }

            if (errorType === 'purchase-limit' && normalizedPurchaseLimitQuantity !== quantity) {
              this.setQuantityInputValues(line, normalizedPurchaseLimitQuantity);
              this.disableLoading(line);
              this.updateQuantity(line, normalizedPurchaseLimitQuantity, event, name, variantId);
              return;
            }

            const fallbackQuantity =
              parsedState.items?.[line - 1]?.quantity ??
              quantityElement?.getAttribute('value') ??
              quantityElement_mobile?.getAttribute('value');

            if (quantityElement && typeof fallbackQuantity !== 'undefined') {
              quantityElement.value = fallbackQuantity;
            }

            if (quantityElement_mobile && typeof fallbackQuantity !== 'undefined') {
              quantityElement_mobile.value = fallbackQuantity;
            }

            this.updateLiveRegions(line, errorMessage || parsedState.errors);
            this.disableLoading(line);
            return;
          }

          // this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartFullWrapper = document.getElementById('cart-full-wrapper');
          if (cartFullWrapper) cartFullWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            if (Array.isArray(section.selector)) {
              section.selector.forEach((sel) => {
                const elementsToReplace = document.getElementById(section.id).querySelectorAll(sel);
                elementsToReplace.forEach((el, idx) => {
                  el.innerHTML = this.getSectionInnerHTML(
                    parsedState.sections[section.section],
                    sel
                  );
                });
              });
            } else {
              const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            }
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    this.querySelectorAll('.loading__spinner')
    .forEach(el => el.classList.add('hidden'));
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);    
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

addEventListener("DOMContentLoaded", () => {
  const html = document.documentElement,
        mainContent = document.getElementById('MainContent'),
        cart_items = document.querySelector('.cart__items-wrapper'),
        cart_footer = document.querySelector('.cart__footer-wrapper');

  if (!cart_items) return


  if (html.getAttribute("data-theme-style") !== "default") {
    const cartFullWrapper = document.createElement("div");
    cartFullWrapper.classList.add(
      "page-width",
      "section-padding",
      "flex",
      "md:flex-row",
      "flex-col",
      "md:gap-3",
      "border-top",
      "border-primary-light",
      "border-solid",
      "js-cart-full-wrapper"
    );
    cartFullWrapper.classList.toggle('is-empty', dataCartCount === 0);
    cartFullWrapper.id = 'cart-full-wrapper';
    cart_items.classList.add(
      "md:w-[66per]",
      "md:min-w-[60per]",
      "sm:p-0",
      "pb-8"
    );
    cart_footer.classList.add(
      "md:w-[32per]",
      "sm:p-0",
      "sm:m-0",
      "pt-8",
      "pb-8",
      "p-lr-20",
      "ml-minus-2,5",
      "mr-minus-2,5",
      "sm:border-0",
      "border-top-bottom",
      "border-solid"
    );
    cartFullWrapper.appendChild(cart_items)
    cartFullWrapper.appendChild(cart_footer)
    
    mainContent.prepend(cartFullWrapper);

    cart_items.querySelector('cart-items').classList.remove('opacity-0');
    cart_footer.querySelector('#main-cart-footer').classList.remove('opacity-0');
  }
});
